import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Stack, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { useUser } from 'store'
import Tree from 'packages/ui/Arborist/components/Tree'
import CardSelection from 'packages/ui/Arborist/components/CardSelection'
import TabPanel from 'components/global/TabPanel'
import { toast } from 'components/global/CUK/Toast/Toast'
import TreeSearch from 'packages/ui/Arborist/components/Search'
import { useTreeContext, useSitesSelected } from 'packages/ui/Arborist/TreeProvider'
import convertAccounts from 'packages/ui/Arborist/utils'
import SearchField from 'packages/ui/Arborist/components/Search/SearchField/SearchField'
import ButtonUnfold from 'packages/ui/Arborist/components/Buttons'
import { useInPerimeter, deserializeSelection } from 'packages/ui/Arborist/utils'
import ActionButtons from './ActionButtons'

const TopBar = ({ disabled, onSearchChange, searchRef }) => {
  const { isFolded, onFoldedToggle } = useTreeContext()

  return (
    <Stack direction="row" gap={2} sx={{ maxHeight: '40px' }}>
      <SearchField disabled={disabled} onChange={onSearchChange} showCountChip={false}  ref={searchRef}/>
      <ButtonUnfold isUnfolded={!isFolded} onClick={onFoldedToggle} />
    </Stack>
  )
}

const Accounts = ({ loading, isLocked, isInternal, onSubmit, onGoBack, contact }) => {
  const {formState: { errors }} = useFormContext()
  const [user] = useUser()
  const disabled = useMemo(() => {
    if (!user) {
      return true
    } else if (!(user.roles.admin_contact || user.admin)) {
      return true
    }
    return false
  }, [user])
  const { accounts, selection, onNodeAdd, onNodeRemove, waiting, treeRef } = useTreeContext()
  const { sites: sitesSelected, count: nbSitesSelected } = useSitesSelected({ selection })
  const checkedAccounts = useInPerimeter()
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()
  const searchFieldRef = useRef(null)
  const { register, setValue, watch } = useFormContext()
  const firstname = watch('first_name')
  const lastname = watch('last_name')
  const accountParentTooltip = useCallback((account) => {
    return t('auth.admin.edit.account_parent_tooltip', {firstname, lastname, account: account.name})
  }, [firstname, lastname])
  const dataTree = useMemo(() => {
    return convertAccounts({ accounts })
  }, [accounts])
  const handleSubmit = () => {
    if (Object.entries(errors).length > 0) {
      toast.error(t('auth.admin.edit.form_errors'));
      return;
    }
    if (nbSitesSelected === 0 && !isInternal) {
      toast.error(t('auth.admin.edit.sites_selected_errors'))
      return;
    }
    onSubmit()
  }

  const handleToast = account => {
    if (!account.is_site) {
      toast.info(
        t('auth.admin.edit.site.toast_info', {
          account_name: account.name,
          contact_name: `${contact.first_name} ${contact.last_name}`
        })
      )
    }
  }

  useEffect(() => {
    const { parent_ids, site_ids } = deserializeSelection(selection)
    setValue('account_ids', { parent_ids, site_ids })
  }, [selection])
  return (
    <TabPanel>
      <Stack sx={{ 
        height: 'calc(100vh - 215px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box flex={1} overflow="auto">
          <input type={'hidden'} {...register('account_ids.parent_ids')} />
          <input type={'hidden'} {...register('account_ids.site_ids')} />
          <Stack spacing={2} sx={{cursor: waiting ? 'wait' : 'inherit'}}>
            <Stack spacing={2}>
              <Stack direction={'row'} spacing={2} sx={{ flex: 1 }}>
                {
                  (user.admin || user.roles.admin_contact) && (
                    <Stack sx={{ flex: 1 }}>
                      <TopBar searchRef={searchFieldRef} disabled={disabled} isUnfolded={true} onSearchChange={setSearchTerm} onToggleUnfolded={() => {}} />
                      {
                        searchTerm.length ? (
                          <TreeSearch
                            hideTopBar={true}
                            ref={treeRef}
                            name={searchTerm}
                            onNodeSelect={(node, checked) => {
                              if (checked) {
                                const account = node.data.data
                                handleToast(account)
                              }
                            }}
                          />
                        ) : (
                          <>
                            <Tree
                              ref={treeRef}
                              data={dataTree}
                              disabled={disabled}
                              openByDefault
                              onNodeSelect={async (node) => {
                                const checked = checkedAccounts.findIndex(account => account.sf_id === node.data.data.sf_id) > -1
                                if (!checked) {
                                  if (!waiting) {
                                    await onNodeAdd(node)
                                    const account = node.data.data
                                    handleToast(account)
                                  }
                                } else {
                                  onNodeRemove(node)
                                }
                              }}
                            />
                          </>
                        )
                      }
                    </Stack>
                  )
                }
                <CardSelection
                  hasAccountParentBadge
                  accountParentTooltip={accountParentTooltip}
                  disabled={disabled}
                  nbSites={nbSitesSelected}
                  accounts={sitesSelected}
                  onRemoveAccount={account => {
                    onNodeRemove({ id: account.sf_id })
                  }}
                  onRemoveAccountParent={account => {
                    onNodeRemove({id: account.sf_id})
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #eee'
        }}>
          <ActionButtons
            loading={loading}
            disabled={isLocked || isInternal || disabled}
            onGoBack={onGoBack}
            onSubmit={handleSubmit}
            labelSubmit={t('ui_elements.form_validate')}
          />
        </Box>
      </Stack>
    </TabPanel>
  )  
}

export default Accounts
